import React, { useEffect, useMemo, useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { Ic, StatusBadge, Modal, SearchableSelect, AutocompleteTextInput, inputStyle, btnPrimary, btnSecondary, btnGhost, showStyledAlert, showStyledConfirm, fmtDate } from "../components/UI";
import { addBeneficiary, updateBeneficiary, deleteBeneficiary } from "../utils/firestore";
import { formatContributionCurrency, getActivityContributionTotal, getActivityContributionValueTotal, sumActivitiesContributionTotal, sumActivitiesContributionValueTotal } from "../utils/activityContributions";

/* ─── Inline Icons ───────────────────────────────────────────── */
function WhatsAppIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.47 14.38c-.27-.14-1.58-.78-1.83-.86-.24-.09-.41-.14-.58.14-.17.27-.67.86-.82 1.03-.15.17-.3.2-.56.07-.27-.14-1.12-.41-2.13-1.3-.79-.7-1.32-1.57-1.47-1.84-.15-.27-.02-.41.11-.55.12-.12.27-.3.41-.45.14-.15.18-.27.27-.45.09-.17.05-.33-.02-.47-.07-.14-.58-1.4-.8-1.92-.21-.5-.43-.43-.58-.44h-.5c-.17 0-.45.07-.68.33-.23.27-.88.86-.88 2.1 0 1.24.9 2.44 1.02 2.61.12.17 1.77 2.7 4.29 3.79 2.52 1.09 2.52.73 2.98.69.45-.04 1.46-.6 1.67-1.17.21-.57.21-1.06.15-1.17-.06-.11-.23-.17-.5-.31Z" />
      <path d="M20.52 3.48A11.87 11.87 0 0 0 12.06 0C5.53 0 .2 5.32.2 11.86c0 2.09.55 4.14 1.6 5.95L0 24l6.36-1.67a11.8 11.8 0 0 0 5.7 1.46h.01c6.54 0 11.87-5.33 11.87-11.87 0-3.17-1.23-6.14-3.42-8.34Zm-8.45 18.31h-.01a9.88 9.88 0 0 1-5.04-1.38l-.36-.22-3.77.99 1.01-3.67-.24-.38a9.86 9.86 0 0 1-1.51-5.27c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.11 1.03 6.97 2.89a9.8 9.8 0 0 1 2.9 6.99c0 5.46-4.44 9.9-9.86 9.9Z" />
    </svg>
  );
}
function UploadIcon({ size = 16 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>);
}
function InfoIcon({ size = 16 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>);
}
function UserPlusIcon({ size = 16 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></svg>);
}
function ChevronRightIcon({ size = 16 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>);
}
function XIcon({ size = 18 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>);
}
function CalendarIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>);
}
function ActivityIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>);
}
function MapPinIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>);
}
function ClockIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>);
}
function NoteIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>);
}
function UsersIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
}
function HomeIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>);
}
function TagIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>);
}
function HeartIcon({ size = 14 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>);
}

/* ─── Shared Style Constants ─────────────────────────────────── */
const ACCENT = "#C41E3A";
const ACCENT_LIGHT = "#FFF1F3";
const BORDER = "#E5E7EB";
const BORDER_LIGHT = "#F3F4F6";
const TEXT_PRIMARY = "#1F2937";
const TEXT_SECONDARY = "#6B7280";
const TEXT_MUTED = "#9CA3AF";
const BG_SUBTLE = "#F9FAFB";

const iconBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, border: `1px solid ${BORDER}`, background: "white", cursor: "pointer", color: TEXT_SECONDARY, transition: "all 0.15s" };
const iconBtnAccent = { ...iconBtn, background: ACCENT, border: `1px solid ${ACCENT}`, color: "white" };
const iconBtnWithLabel = { ...iconBtn, width: "auto", padding: "0 14px", gap: 6, fontSize: 13, fontWeight: 500, fontFamily: "Heebo" };

/* ─── Beneficiary Drawer ─────────────────────────────────────── */
function BeneficiaryDrawer({ item, onClose, onEdit, onDelete, onAddActivity, domainLabel, stats, volMap, coordinatorNameById, isMobile }) {
  const [closing, setClosing] = useState(false);
  const handleClose = useCallback(() => { setClosing(true); setTimeout(() => onClose(), 260); }, [onClose]);

  const normalizePhone = (phone) => `${phone || ""}`.replace(/[^\d+]/g, "");
  const toWhatsappNumber = (phone) => {
    const digits = `${phone || ""}`.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("972")) return digits;
    if (digits.startsWith("0")) return `972${digits.slice(1)}`;
    return digits;
  };
  const telHref = normalizePhone(item.phone) ? `tel:${normalizePhone(item.phone)}` : "";
  const waHref = toWhatsappNumber(item.phone) ? `https://wa.me/${toWhatsappNumber(item.phone)}` : "";
  const secondaryTelHref = normalizePhone(item.secondaryPhone) ? `tel:${normalizePhone(item.secondaryPhone)}` : "";
  const secondaryWaHref = toWhatsappNumber(item.secondaryPhone) ? `https://wa.me/${toWhatsappNumber(item.secondaryPhone)}` : "";
  const drawerWidth = isMobile ? "100vw" : 440;
  const latestRows = (stats?.rows || []).slice(0, 10);
  const recurringRows = stats?.recurringRows || [];

  const InfoRow = ({ icon, label, value, children }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
      <div style={{ color: TEXT_MUTED, flexShrink: 0, marginTop: 2 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_MUTED, marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
        {children || <div style={{ fontSize: 14, color: TEXT_PRIMARY, wordBreak: "break-word" }}>{value || "—"}</div>}
      </div>
    </div>
  );

  return (
    <>
      <div onClick={handleClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9998, opacity: closing ? 0 : 1, transition: "opacity 0.25s ease", backdropFilter: "blur(2px)" }} />
      <div dir="rtl" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: drawerWidth, maxWidth: "100vw", background: "white", zIndex: 9999, boxShadow: "-8px 0 30px rgba(0,0,0,0.12)", transform: closing ? "translateX(100%)" : "translateX(0)", transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)", display: "flex", flexDirection: "column", animation: "drawerSlideIn 0.28s cubic-bezier(0.4, 0, 0.2, 1)" }}>
        <style>{`@keyframes drawerSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${BORDER}`, background: BG_SUBTLE }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <button onClick={handleClose} style={{ ...iconBtn, width: 32, height: 32 }}><XIcon size={16} /></button>
            <div style={{ display: "flex", gap: 6 }}>
              <button style={iconBtn} onClick={() => onEdit(item)} title="עריכה"><Ic.Edit /></button>
              <button style={{ ...iconBtn, color: "#EF4444" }} onClick={() => onDelete(item.id)} title="מחיקה"><Ic.Trash /></button>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, flexShrink: 0, background: "linear-gradient(135deg, #7C3AED, #A78BFA)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, fontFamily: "Assistant", boxShadow: "0 4px 12px rgba(124,58,237,0.3)" }}>
              {(item.name || "?")[0]}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "Assistant", color: TEXT_PRIMARY, marginBottom: 4 }}>{item.name || "ללא שם"}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <StatusBadge status={item.active ? "active" : "inactive"} />
                {item.city && <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>{item.city}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", borderBottom: `1px solid ${BORDER}` }}>
          {[
            { label: "פעמים נעזר", value: stats.count, icon: <HeartIcon size={13} />, color: ACCENT },
            { label: "סה״כ שעות", value: stats.totalHours.toFixed(1), icon: <ClockIcon size={13} />, color: "#3B82F6" },
            { label: "תרומות", value: stats.totalContributions || 0, icon: <ActivityIcon size={13} />, color: "#C2410C" },
            { label: "שווי תרומות", value: formatContributionCurrency(stats.totalContributionValue || 0) || "—", icon: <ActivityIcon size={13} />, color: "#0F766E", small: true },
            { label: "סיוע אחרון", value: stats.latestDate || "—", icon: <CalendarIcon size={13} />, color: "#10B981", small: true },
          ].map((s, i) => (
            <div key={i} style={{ padding: "14px 8px", textAlign: "center", borderLeft: i < 4 ? `1px solid ${BORDER_LIGHT}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, color: s.color, marginBottom: 4 }}>
                {s.icon}<span style={{ fontSize: s.small ? 13 : 18, fontWeight: 800, fontFamily: "Assistant" }}>{s.value}</span>
              </div>
              <div style={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div style={{ display: "flex", gap: 8, padding: "14px 24px", borderBottom: `1px solid ${BORDER}` }}>
          {onAddActivity && (
            <button
              type="button"
              onClick={() => onAddActivity(item)}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", borderRadius: 10, background: ACCENT_LIGHT, color: ACCENT, textDecoration: "none", fontSize: 13, fontWeight: 600, fontFamily: "Heebo", border: `1px solid ${ACCENT}` }}
            >
              <Ic.Plus /> פעילות
            </button>
          )}
          {telHref && (
            <a href={telHref} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", borderRadius: 10, background: "#EFF6FF", color: "#2563EB", textDecoration: "none", fontSize: 13, fontWeight: 600, fontFamily: "Heebo", border: "1px solid #DBEAFE" }}>
              <Ic.Phone /> התקשרות
            </a>
          )}
          {waHref && (
            <a href={waHref} target="_blank" rel="noreferrer" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "10px 0", borderRadius: 10, background: "#F0FDF4", color: "#16A34A", textDecoration: "none", fontSize: 13, fontWeight: 600, fontFamily: "Heebo", border: "1px solid #DCFCE7" }}>
              <WhatsAppIcon size={14} /> וואטסאפ
            </a>
          )}
        </div>

        {/* Details + History */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 24px 24px" }}>
          <InfoRow icon={<Ic.Phone />} label="טלפון" value={item.phone} />
          {(item.secondaryPhone || item.secondaryContactName) && (
            <InfoRow icon={<Ic.Phone />} label="איש קשר נוסף">
              <div style={{ fontSize: 13, color: TEXT_PRIMARY, display: "flex", flexDirection: "column", gap: 4 }}>
                <div>{item.secondaryContactName || "ללא שם"}</div>
                {item.secondaryPhone && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ direction: "ltr", textAlign: "right" }}>{item.secondaryPhone}</span>
                    {secondaryTelHref && <a href={secondaryTelHref} style={{ color: "#2563EB", textDecoration: "none" }}>התקשרות</a>}
                    {secondaryWaHref && <a href={secondaryWaHref} target="_blank" rel="noreferrer" style={{ color: "#16A34A", textDecoration: "none" }}>וואטסאפ</a>}
                  </div>
                )}
              </div>
            </InfoRow>
          )}
          <InfoRow icon={<HomeIcon />} label="כתובת" value={item.address} />
          <InfoRow icon={<MapPinIcon />} label="עיר" value={item.city} />
          <InfoRow icon={<TagIcon />} label="תחום" value={domainLabel(item.domain)} />
          <InfoRow icon={<UsersIcon />} label="רכז משויך" value={coordinatorNameById[item.coordinatorId] || "—"} />
          <InfoRow icon={<UsersIcon />} label="מי קלט את הנעזר" value={item.intakeCoordinatorName || coordinatorNameById[item.intakeCoordinatorId] || item.createdByName || "—"} />
          <InfoRow icon={<UsersIcon />} label="על ידי מי">
            <div style={{ fontSize: 13, color: TEXT_PRIMARY }}>
              {stats.helperNames.length ? stats.helperNames.join(", ") : <span style={{ color: TEXT_MUTED }}>אין עדיין פעילויות</span>}
            </div>
          </InfoRow>
          {item.notes && <InfoRow icon={<NoteIcon />} label="הערות" value={item.notes} />}

          {recurringRows.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <ActivityIcon size={14} /> פעילויות קבועות משויכות
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {recurringRows.map((row) => (
                  <div key={row.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: BG_SUBTLE, border: `1px solid ${BORDER_LIGHT}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: TEXT_PRIMARY, fontWeight: 600 }}>{row.label}</div>
                      <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 2 }}>{row.schedule}</div>
                    </div>
                    <div style={{ fontSize: 12, color: ACCENT, fontWeight: 800, whiteSpace: "nowrap" }}>{row.count} שימושים</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity History */}
          {latestRows.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <ActivityIcon size={14} /> היסטוריית פעילויות אחרונות
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {latestRows.map((a) => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: BG_SUBTLE, border: `1px solid ${BORDER_LIGHT}` }}>
                    <div style={{ fontSize: 12, color: TEXT_SECONDARY, minWidth: 70 }}>{a.date || "—"}</div>
                    <div style={{ flex: 1, fontSize: 13, color: TEXT_PRIMARY }}>{volMap[a.volunteerId]?.name || "ללא מתנדב"}</div>
                    <div style={{ fontSize: 12, color: TEXT_MUTED, textAlign: "left" }}>
                      {Number(a.duration || 0)} ש'
                      {getActivityContributionTotal(a) > 0 ? ` · ${getActivityContributionTotal(a)} תרומות` : ""}
                      {getActivityContributionValueTotal(a) > 0 ? ` · ${formatContributionCurrency(getActivityContributionValueTotal(a))}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function BeneficiariesPage({ volunteers, domains, activities = [], recurring = [], showToast, isMobile = false, addOpenToken = 0, coordinators = [], currentUser = null, canAddBeneficiaries = true, canAddActivities = true, onAddActivity, onQuickAddComplete }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [drawerItem, setDrawerItem] = useState(null);
  const [search, setSearch] = useState("");
  const [fFromDate, setFFromDate] = useState("");
  const [fToDate, setFToDate] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [importing, setImporting] = useState(false);
  const [openedFromQuickAction, setOpenedFromQuickAction] = useState(false);
  const importInputRef = React.useRef(null);

  React.useEffect(() => {
    let alive = true;
    async function load() {
      try {
        setLoading(true);
        const { getBeneficiaries } = await import("../utils/firestore");
        const rows = await getBeneficiaries();
        if (alive) setItems(rows);
      } catch (e) { await showStyledAlert("שגיאה בטעינת נעזרים: " + e.message); }
      finally { if (alive) setLoading(false); }
    }
    load();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (addOpenToken > 0) {
      setOpenedFromQuickAction(true);
      setModal({ type: "add" });
    }
  }, [addOpenToken]);

  const activeDomains = useMemo(() => domains.filter((d) => d.active), [domains]);
  const domainLabel = (domainId) => activeDomains.find((d) => d.id === domainId)?.name || domains.find((d) => d.id === domainId)?.name || domainId || "—";
  const volMap = useMemo(() => { const m = {}; volunteers.forEach((v) => { m[v.id] = v; }); return m; }, [volunteers]);
  const recurringMap = useMemo(() => {
    const map = {};
    recurring.forEach((item) => {
      if (item?.id) map[item.id] = item;
    });
    return map;
  }, [recurring]);
  const coordinatorLookup = useMemo(() => {
    const norm = (v) => (v || "").trim().toLowerCase();
    const map = new Map();
    coordinators.forEach((c) => { if (!c) return; const cid = (c.id || "").trim(); if (!cid) return; map.set(norm(cid), cid); if (c.email) map.set(norm(c.email), cid); if (c.name) map.set(norm(c.name), cid); });
    return map;
  }, [coordinators]);
  const coordinatorNameById = useMemo(() => { const m = {}; coordinators.forEach((c) => { if (c?.id) m[c.id] = c.name || c.email || c.id; }); return m; }, [coordinators]);
  const domainLookup = useMemo(() => {
    const norm = (v) => (v || "").trim().toLowerCase();
    const map = new Map();
    (activeDomains.length ? activeDomains : domains).forEach((d) => {
      if (!d?.id) return;
      map.set(norm(d.id), d.id);
      map.set(norm(d.name), d.id);
    });
    return map;
  }, [activeDomains, domains]);

  const normalizePhone = (phone) => `${phone || ""}`.replace(/[^\d+]/g, "");
  const toWhatsappNumber = (phone) => {
    const digits = `${phone || ""}`.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("972")) return digits;
    if (digits.startsWith("0")) return `972${digits.slice(1)}`;
    return digits;
  };
  const getPhoneLinks = (phone) => {
    const telRaw = normalizePhone(phone);
    const waRaw = toWhatsappNumber(phone);
    return { telHref: telRaw ? `tel:${telRaw}` : "", waHref: waRaw ? `https://wa.me/${waRaw}` : "" };
  };

  const applyDatePreset = useCallback((preset) => {
    const now = new Date();
    const currentDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (preset === "custom") {
      setDatePreset("custom");
      return;
    }
    if (preset === "all") {
      setFFromDate("");
      setFToDate("");
      setDatePreset("all");
      return;
    }
    if (preset === "today") {
      const d = fmtDate(currentDay);
      setFFromDate(d);
      setFToDate(d);
      setDatePreset("today");
      return;
    }
    if (preset === "month") {
      const start = new Date(currentDay.getFullYear(), currentDay.getMonth(), 1);
      setFFromDate(fmtDate(start));
      setFToDate(fmtDate(currentDay));
      setDatePreset("month");
    }
  }, []);

  const activitiesInRange = useMemo(() => {
    return activities.filter((a) => {
      const bid = (a.beneficiaryId || "").trim();
      if (!bid) return false;
      const date = (a.date || "").trim();
      if (fFromDate && (!date || date < fFromDate)) return false;
      if (fToDate && (!date || date > fToDate)) return false;
      return true;
    });
  }, [activities, fFromDate, fToDate]);

  const activitiesByBeneficiary = useMemo(() => {
    const grouped = {};
    activitiesInRange.forEach((a) => { const bid = (a.beneficiaryId || "").trim(); if (!bid) return; if (!grouped[bid]) grouped[bid] = []; grouped[bid].push(a); });
    Object.keys(grouped).forEach((k) => { grouped[k].sort((a, b) => `${b.date || ""} ${b.time || ""}`.localeCompare(`${a.date || ""} ${a.time || ""}`)); });
    return grouped;
  }, [activitiesInRange]);

  const refresh = async () => { const { getBeneficiaries } = await import("../utils/firestore"); setItems(await getBeneficiaries()); };

  const getBeneficiaryStats = (beneficiaryId) => {
    const rows = activitiesByBeneficiary[beneficiaryId] || [];
    const totalHours = rows.reduce((sum, row) => sum + Number(row.duration || 0), 0);
    const totalContributions = sumActivitiesContributionTotal(rows);
    const totalContributionValue = sumActivitiesContributionValueTotal(rows);
    const byVolunteer = {};
    rows.forEach((row) => { const key = row.volunteerId || "none"; byVolunteer[key] = (byVolunteer[key] || 0) + 1; });
    const helperNames = Object.entries(byVolunteer).sort((a, b) => b[1] - a[1]).map(([volunteerId, count]) => `${volMap[volunteerId]?.name || "ללא מתנדב"} (${count})`);
    const byRecurring = {};
    rows.forEach((row) => {
      const recurringId = (row.recurringId || "").trim();
      if (!recurringId) return;
      byRecurring[recurringId] = (byRecurring[recurringId] || 0) + 1;
    });
    const recurringRows = Object.entries(byRecurring)
      .map(([recurringId, count]) => {
        const recurringItem = recurringMap[recurringId];
        const volunteerName = recurringItem?.volunteerId ? (volMap[recurringItem.volunteerId]?.name || "ללא מתנדב") : "ללא מתנדב";
        const domainName = recurringItem?.domainId ? domainLabel(recurringItem.domainId) : "ללא תחום";
        return {
          id: recurringId,
          count,
          label: `${domainName} · ${volunteerName}`,
          schedule: recurringItem ? `יום ${["א", "ב", "ג", "ד", "ה", "ו", "ש"][Number(recurringItem.day) || 0]} · ${recurringItem.time || "—"}` : "פעילות קבועה",
        };
      })
      .sort((a, b) => b.count - a.count);
    const latest = rows[0] || null;
    return { count: rows.length, totalHours, totalContributions, totalContributionValue, helperNames, latestDate: latest?.date || "", rows, recurringRows };
  };

  const readCell = (row, aliases) => {
    const norm = (v) => (v || "").toString().trim().toLowerCase().replace(/\s+/g, " ");
    const keyMap = new Map(Object.keys(row || {}).map((k) => [norm(k), row[k]]));
    for (const alias of aliases) { const value = keyMap.get(norm(alias)); if (value !== undefined && value !== null && `${value}`.trim() !== "") return `${value}`.trim(); }
    return "";
  };

  const showImportInstructions = async () => {
    await showStyledAlert("הוראות ייבוא נעזרים מאקסל:\n\n1) יש להשתמש בגיליון הראשון בקובץ.\n2) כותרות מומלצות: שם, טלפון, עיר, כתובת, רכז משויך.\n3) חובה שדה שם. שורות ללא שם ידולגו.\n4) רכז משויך מזוהה לפי שם/אימייל/מזהה רכז. אם לא נמצא - יישאר ריק.\n5) אפשר קובץ .xlsx / .xls / .csv");
  };

  const runBeneficiariesImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      if (!rows.length) { await showStyledAlert("הקובץ ריק או שאין בו שורות נתונים."); return; }
      let created = 0, skipped = 0, unresolvedCoordinator = 0, unresolvedDomain = 0, failed = 0;
      for (const row of rows) {
        const name = readCell(row, ["שם", "שם מלא", "name"]);
        if (!name) { skipped += 1; continue; }
        const coordinatorRaw = readCell(row, ["רכז משויך", "רכז", "coordinator", "coordinator email", "רכז אימייל"]);
        const coordinatorId = coordinatorRaw ? (coordinatorLookup.get(coordinatorRaw.trim().toLowerCase()) || "") : "";
        if (coordinatorRaw && !coordinatorId) unresolvedCoordinator += 1;
        const domainRaw = readCell(row, ["תחום", "domain", "קטגוריה", "תת תחום", "תת-תחום"]);
        const resolvedDomainId = domainRaw ? (domainLookup.get(domainRaw.trim().toLowerCase()) || "") : "";
        if (domainRaw && !resolvedDomainId) unresolvedDomain += 1;
        try {
          await addBeneficiary({
            name,
            phone: readCell(row, ["טלפון", "phone", "mobile"]),
            city: readCell(row, ["עיר", "city"]),
            address: readCell(row, ["כתובת", "address"]),
            secondaryContactName: readCell(row, ["איש קשר נוסף", "איש קשר", "שם איש קשר נוסף", "secondary contact"]),
            secondaryPhone: readCell(row, ["טלפון נוסף", "טלפון איש קשר נוסף", "secondary phone"]),
            coordinatorId,
            domain: resolvedDomainId || activeDomains[0]?.id || domains[0]?.id || "other",
            volunteerIds: [],
            active: true,
            notes: readCell(row, ["הערות", "notes"]),
          });
          created += 1;
        } catch (rowError) {
          console.warn("Beneficiary import row failed:", rowError);
          failed += 1;
        }
      }
      await refresh();
      showToast(`ייבוא נעזרים הושלם: ${created} נוספו`);
      await showStyledAlert(
        `הייבוא הסתיים.\nנוספו: ${created}\nדולגו (ללא שם): ${skipped}\nשגיאות בשורות: ${failed}\nרכז לא נמצא והושאר ריק: ${unresolvedCoordinator}\nתחום לא נמצא והושאר ברירת מחדל: ${unresolvedDomain}`
      );
    } catch (e) { await showStyledAlert("שגיאה בייבוא נעזרים: " + e.message); }
    finally { setImporting(false); if (importInputRef.current) importInputRef.current.value = ""; }
  };

  const save = async (form) => {
    try {
      if (modal?.type === "edit") { await updateBeneficiary(modal.item.id, form); showToast("נעזר עודכן"); }
      else {
        await addBeneficiary(form);
        showToast("נעזר נוסף");
        if (openedFromQuickAction && typeof onQuickAddComplete === "function") {
          setOpenedFromQuickAction(false);
          onQuickAddComplete();
        }
      }
      setModal(null); await refresh();
    } catch (e) { await showStyledAlert("שגיאה בשמירת נעזר: " + e.message); }
  };

  const remove = async (id) => {
    if (!await showStyledConfirm("למחוק נעזר?")) return;
    try { await deleteBeneficiary(id); showToast("נעזר נמחק"); setDrawerItem(null); await refresh(); }
    catch (e) { await showStyledAlert("שגיאה במחיקת נעזר: " + e.message); }
  };

  const filteredItems = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((b) => [b.name || "", b.phone || "", b.secondaryPhone || "", b.secondaryContactName || "", b.city || "", b.address || ""].join(" ").toLowerCase().includes(q));
  }, [items, search]);

  const summary = useMemo(() => {
    const uniqueBeneficiaryIds = new Set(activitiesInRange.map((a) => (a.beneficiaryId || "").trim()).filter(Boolean));
    const totalCases = activitiesInRange.length;
    const totalHours = activitiesInRange.reduce((sum, a) => sum + Number(a.duration || 0), 0);
    const totalContributions = sumActivitiesContributionTotal(activitiesInRange);
    const totalContributionValue = sumActivitiesContributionValueTotal(activitiesInRange);
    return {
      beneficiariesCount: filteredItems.length,
      assistedBeneficiariesInRange: uniqueBeneficiaryIds.size,
      totalCases,
      totalHours,
      totalContributions,
      totalContributionValue,
    };
  }, [activitiesInRange, filteredItems]);

  return (
    <div>
      {/* ── Toolbar ──────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: BG_SUBTLE, borderRadius: 10, padding: "8px 14px", width: isMobile ? "100%" : 260, border: `1px solid ${BORDER}` }}>
            <Ic.Search />
            <input placeholder="חיפוש נעזר..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ border: "none", background: "none", outline: "none", fontSize: 14, width: "100%", direction: "rtl", fontFamily: "Heebo" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {[
              { id: "all", label: "הכל" },
              { id: "today", label: "היום" },
              { id: "month", label: "החודש" },
              { id: "custom", label: "טווח" },
            ].map((opt) => {
              const active = datePreset === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => applyDatePreset(opt.id)}
                  style={{
                    border: active ? `1px solid ${ACCENT}` : `1px solid ${BORDER}`,
                    background: active ? ACCENT_LIGHT : "white",
                    color: active ? ACCENT : TEXT_SECONDARY,
                    borderRadius: 10,
                    padding: "7px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "Heebo",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {datePreset === "custom" && (
            <>
              <input
                type="date"
                value={fFromDate}
                onChange={(e) => { setDatePreset("custom"); setFFromDate(e.target.value); }}
                style={{ ...inputStyle, borderRadius: 10, padding: "8px 10px", minWidth: 150 }}
              />
              <input
                type="date"
                value={fToDate}
                onChange={(e) => { setDatePreset("custom"); setFToDate(e.target.value); }}
                style={{ ...inputStyle, borderRadius: 10, padding: "8px 10px", minWidth: 150 }}
              />
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button style={iconBtn} onClick={showImportInstructions} title="הוראות ייבוא"><InfoIcon /></button>
          <button style={iconBtn} onClick={() => importInputRef.current?.click()} disabled={importing} title="ייבוא מאקסל">
            {importing ? <span style={{ fontSize: 12 }}>...</span> : <UploadIcon />}
          </button>
          {canAddBeneficiaries && <button style={iconBtnAccent} onClick={() => { setOpenedFromQuickAction(false); setModal({ type: "add" }); }} title="נעזר חדש"><UserPlusIcon size={17} /></button>}
        </div>
      </div>

      <input ref={importInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={(e) => runBeneficiariesImport(e.target.files?.[0])} />

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, minmax(0,1fr))", gap: 10, marginBottom: 14 }}>
        {[
          { label: "סה״כ נעזרים", value: summary.beneficiariesCount, color: ACCENT, sub: search ? "לפי חיפוש" : "במערכת" },
          { label: "נעזרים בתקופה", value: summary.assistedBeneficiariesInRange, color: "#2563EB", sub: "ייחודיים עם פעילות" },
          { label: "פעמים נעזרו", value: summary.totalCases, color: "#16A34A", sub: "מספר מקרים" },
          { label: "סה״כ שעות", value: summary.totalHours.toFixed(1), color: "#7C3AED", sub: "בתקופה שנבחרה" },
          { label: "סה״כ תרומות", value: summary.totalContributions, color: "#C2410C", sub: "בתקופה שנבחרה" },
          { label: "שווי תרומות", value: formatContributionCurrency(summary.totalContributionValue) || "—", color: "#0F766E", sub: "בתקופה שנבחרה" },
        ].map((card) => (
          <div key={card.label} style={{ background: "white", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 6 }}>{card.label}</div>
            <div style={{ fontFamily: "Assistant", fontSize: 22, fontWeight: 800, color: card.color, lineHeight: 1.1 }}>{card.value}</div>
            <div style={{ fontSize: 11, color: TEXT_SECONDARY, marginTop: 4 }}>{card.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Results ───────────────────────────────────────────── */}
      <div style={{ background: "white", borderRadius: 14, border: `1px solid ${BORDER}`, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "12px 16px" : "14px 20px", borderBottom: `1px solid ${BORDER}`, background: BG_SUBTLE }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_SECONDARY, fontFamily: "Heebo" }}>
            {filteredItems.length} נעזרים
          </span>
          {search && (
            <button onClick={() => setSearch("")} style={{ ...btnGhost, fontSize: 12, color: ACCENT }}>נקה חיפוש</button>
          )}
        </div>

        {/* Desktop table */}
        {!isMobile ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["שם", "כתובת", "טלפון", "תחום", "פעמים נעזר", "סטטוס", ""].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 600, color: TEXT_MUTED, textAlign: "right", background: "white", borderBottom: `1px solid ${BORDER}`, textTransform: "uppercase", letterSpacing: "0.3px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: TEXT_MUTED }}>טוען...</td></tr>}
              {!loading && filteredItems.map((b) => {
                const stats = getBeneficiaryStats(b.id);
                const links = getPhoneLinks(b.phone);
                return (
                  <tr key={b.id} style={{ cursor: "pointer", transition: "background 0.1s" }} onClick={() => setDrawerItem(b)} onMouseEnter={e => e.currentTarget.style.background = BG_SUBTLE} onMouseLeave={e => e.currentTarget.style.background = "white"}>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, background: "linear-gradient(135deg, #7C3AED, #A78BFA)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, fontFamily: "Assistant" }}>
                          {(b.name || "?")[0]}
                        </div>
                        <div>
                          <span style={{ fontWeight: 600, fontSize: 14, color: TEXT_PRIMARY }}>{b.name || "—"}</span>
                          {b.city && <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 1 }}>{b.city}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: 13, color: TEXT_SECONDARY }}>{b.address || "—"}</td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 13, color: TEXT_SECONDARY, direction: "ltr" }}>{b.phone || b.secondaryPhone || "—"}</span>
                        {links.waHref && (
                          <a href={links.waHref} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} title="וואטסאפ" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 6, border: "1px solid #DCFCE7", color: "#16A34A", background: "#F0FDF4" }}>
                            <WhatsAppIcon size={11} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: 13, color: TEXT_SECONDARY }}>{domainLabel(b.domain)}</td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <HeartIcon size={12} />
                        <span style={{ fontFamily: "Assistant", fontWeight: 800, color: ACCENT, fontSize: 14 }}>{stats.count}</span>
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}><StatusBadge status={b.active ? "active" : "inactive"} /></td>
                    <td style={{ padding: "12px 16px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <div style={{ display: "flex", alignItems: "center", color: TEXT_MUTED }}><ChevronRightIcon size={16} /></div>
                    </td>
                  </tr>
                );
              })}
              {!loading && filteredItems.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: TEXT_MUTED }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>אין נעזרים להצגה
                </td></tr>
              )}
            </tbody>
          </table>
        ) : (
          /* Mobile cards */
          <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            {loading && <div style={{ textAlign: "center", padding: 32, color: TEXT_MUTED }}>טוען...</div>}
            {!loading && filteredItems.map((b) => {
              const stats = getBeneficiaryStats(b.id);
              const links = getPhoneLinks(b.phone);
              return (
                <div key={b.id} onClick={() => setDrawerItem(b)} style={{ border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14, cursor: "pointer", background: "white", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: "linear-gradient(135deg, #7C3AED, #A78BFA)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 700, fontFamily: "Assistant" }}>
                    {(b.name || "?")[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: TEXT_PRIMARY, fontFamily: "Assistant" }}>{b.name || "—"}</span>
                      <StatusBadge status={b.active ? "active" : "inactive"} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: TEXT_SECONDARY, marginBottom: 6 }}>
                      {b.city && <span>{b.city}</span>}
                      {b.city && b.phone && <span style={{ color: BORDER }}>|</span>}
                      {(b.phone || b.secondaryPhone) && <span style={{ direction: "ltr" }}>{b.phone || b.secondaryPhone}</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>{domainLabel(b.domain)}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <HeartIcon size={11} />
                        <span style={{ fontWeight: 700, color: ACCENT, fontSize: 13 }}>{stats.count}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {links.telHref && (
                      <a href={links.telHref} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 9, border: "1px solid #DBEAFE", color: "#2563EB", background: "#EFF6FF" }}><Ic.Phone /></a>
                    )}
                    {links.waHref && (
                      <a href={links.waHref} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 9, border: "1px solid #DCFCE7", color: "#16A34A", background: "#F0FDF4" }}><WhatsAppIcon size={14} /></a>
                    )}
                  </div>
                </div>
              );
            })}
            {!loading && filteredItems.length === 0 && (
              <div style={{ textAlign: "center", padding: 40, color: TEXT_MUTED }}><div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>אין נעזרים להצגה</div>
            )}
          </div>
        )}
      </div>

      {/* Drawer */}
      {drawerItem && (
        <BeneficiaryDrawer
          item={drawerItem}
          onClose={() => setDrawerItem(null)}
          onEdit={(b) => { setDrawerItem(null); setModal({ type: "edit", item: b }); }}
          onDelete={remove}
          onAddActivity={canAddActivities && onAddActivity ? (beneficiary) => {
            setDrawerItem(null);
            onAddActivity({
              beneficiaryId: beneficiary.id,
              domainId: beneficiary.domain || "",
              city: beneficiary.city || "",
            });
          } : null}
          domainLabel={domainLabel}
          stats={getBeneficiaryStats(drawerItem.id)}
          volMap={volMap}
          coordinatorNameById={coordinatorNameById}
          isMobile={isMobile}
        />
      )}

      {/* Form Modal */}
      {modal && (
        <BeneficiaryFormModal
          item={modal.item}
          volunteers={volunteers}
          beneficiaries={items}
          domains={domains}
          coordinators={coordinators}
          currentUser={currentUser}
          onClose={() => setModal(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

/* ─── Form Modal ─────────────────────────────────────────────── */
function BeneficiaryFormModal({ item, volunteers, beneficiaries = [], domains, coordinators = [], currentUser = null, onClose, onSave }) {
  const activeDomains = domains.filter((d) => d.active);
  const activeVolunteers = volunteers.filter((v) => v.status === "active");
  const activeCoordinators = coordinators.filter((coordinator) => coordinator?.id);
  const cityOptions = useMemo(() => Array.from(new Set(
    [
      ...beneficiaries.map((beneficiary) => (beneficiary?.city || "").trim()),
      ...volunteers.map((volunteer) => (volunteer?.city || "").trim()),
      ...domains.map((domain) => (domain?.city || "").trim()),
    ].filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, "he")), [beneficiaries, domains, volunteers]);
  const [f, setF] = useState({
    name: item?.name || "",
    address: item?.address || "",
    city: item?.city || "",
    phone: item?.phone || "",
    secondaryContactName: item?.secondaryContactName || "",
    secondaryPhone: item?.secondaryPhone || "",
    domain: item?.domain || activeDomains[0]?.id || domains[0]?.id || "",
    coordinatorId: item?.coordinatorId || "",
    intakeCoordinatorMode: item?.intakeCoordinatorMode || "auto",
    intakeCoordinatorId: item?.intakeCoordinatorId || "",
    intakeCoordinatorName: item?.intakeCoordinatorName || "",
    createdByUserId: item?.createdByUserId || currentUser?.id || "",
    createdByName: item?.createdByName || currentUser?.name || currentUser?.email || "",
    volunteerIds: item?.volunteerIds || [],
    active: item?.active ?? true,
    notes: item?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [selectedVolunteerId, setSelectedVolunteerId] = useState("");

  const u = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const addVolunteerToBeneficiary = () => {
    const id = (selectedVolunteerId || "").trim();
    if (!id) return;
    setF((p) => (p.volunteerIds.includes(id) ? p : { ...p, volunteerIds: [...p.volunteerIds, id] }));
    setSelectedVolunteerId("");
  };
  const removeVolunteerFromBeneficiary = (id) => {
    setF((p) => ({ ...p, volunteerIds: p.volunteerIds.filter((x) => x !== id) }));
  };

  const submit = async () => {
    const autoIntakeName = currentUser?.name || currentUser?.email || "";
    const autoIntakeId = currentUser?.id || "";
    const manualCoordinator = activeCoordinators.find((coordinator) => coordinator.id === f.intakeCoordinatorId);
    const nextForm = {
      ...f,
      coordinatorId: (f.coordinatorId || "").trim(),
      intakeCoordinatorMode: f.intakeCoordinatorMode === "manual" ? "manual" : "auto",
      intakeCoordinatorId: f.intakeCoordinatorMode === "manual" ? (f.intakeCoordinatorId || "").trim() : autoIntakeId,
      intakeCoordinatorName: f.intakeCoordinatorMode === "manual" ? (manualCoordinator?.name || manualCoordinator?.email || f.intakeCoordinatorName || "") : autoIntakeName,
      createdByUserId: f.createdByUserId || autoIntakeId,
      createdByName: f.createdByName || autoIntakeName,
    };
    setSaving(true);
    await onSave(nextForm);
    setSaving(false);
  };

  const importFromPhoneContact = async () => {
    try {
      if (!("contacts" in navigator) || !navigator.contacts?.select) { await showStyledAlert("הדפדפן/מכשיר לא תומך בגישה לאנשי קשר."); return; }
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: false });
      if (!picked || !picked.length) return;
      const c = picked[0];
      const fullName = getContactValue(c?.name);
      const phone = getContactValue(c?.tel);
      setF((p) => ({ ...p, name: fullName || p.name, phone: phone || p.phone }));
    } catch (e) { await showStyledAlert("שגיאה בייבוא מאנשי קשר: " + e.message); }
  };

  const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: TEXT_SECONDARY, marginBottom: 6 };

  return (
    <Modal title={item ? "עריכת נעזר" : "הוספת נעזר"} onClose={onClose} footer={<><button style={btnPrimary} onClick={submit} disabled={saving}><Ic.Check />{saving ? "שומר..." : "שמירה"}</button><button style={btnSecondary} onClick={onClose}>ביטול</button></>}>
      {!item && (
        <div style={{ marginBottom: 12 }}>
          <button type="button" style={btnSecondary} onClick={importFromPhoneContact}>ייבוא מאנשי קשר בטלפון</button>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>שם</label><input style={inputStyle} value={f.name} onChange={(e) => u("name", e.target.value)} /></div>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>טלפון</label><input style={{ ...inputStyle, direction: "ltr", textAlign: "right" }} value={f.phone} onChange={(e) => u("phone", e.target.value)} /></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>איש קשר נוסף (שם)</label><input style={inputStyle} value={f.secondaryContactName || ""} onChange={(e) => u("secondaryContactName", e.target.value)} /></div>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>איש קשר נוסף (טלפון)</label><input style={{ ...inputStyle, direction: "ltr", textAlign: "right" }} value={f.secondaryPhone || ""} onChange={(e) => u("secondaryPhone", e.target.value)} /></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>כתובת</label><input style={inputStyle} value={f.address} onChange={(e) => u("address", e.target.value)} /></div>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>עיר</label><AutocompleteTextInput value={f.city} onChange={(nextCity) => u("city", nextCity)} options={cityOptions} placeholder="לדוגמה: נתיבות" emptyText="אין ערים תואמות" /></div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>תחום</label>
        <select style={{ ...inputStyle, cursor: "pointer" }} value={f.domain} onChange={(e) => u("domain", e.target.value)}>
          {(activeDomains.length ? activeDomains : domains).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>רכז משויך</label>
        <select style={{ ...inputStyle, cursor: "pointer" }} value={f.coordinatorId || ""} onChange={(e) => u("coordinatorId", e.target.value)}>
          <option value="">ללא רכז משויך</option>
          {activeCoordinators.map((coordinator) => <option key={coordinator.id} value={coordinator.id}>{coordinator.name || coordinator.email || coordinator.id}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>מי קלט את הנעזר</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <button type="button" style={f.intakeCoordinatorMode !== "manual" ? btnPrimary : btnSecondary} onClick={() => u("intakeCoordinatorMode", "auto")}>אוטומטי</button>
          <button type="button" style={f.intakeCoordinatorMode === "manual" ? btnPrimary : btnSecondary} onClick={() => u("intakeCoordinatorMode", "manual")}>ידני</button>
        </div>
        {f.intakeCoordinatorMode === "manual" ? (
          <select style={{ ...inputStyle, cursor: "pointer" }} value={f.intakeCoordinatorId || ""} onChange={(e) => u("intakeCoordinatorId", e.target.value)}>
            <option value="">בחר רכז</option>
            {activeCoordinators.map((coordinator) => <option key={coordinator.id} value={coordinator.id}>{coordinator.name || coordinator.email || coordinator.id}</option>)}
          </select>
        ) : (
          <div style={{ ...inputStyle, background: BG_SUBTLE, color: TEXT_PRIMARY }}>
            {currentUser?.name || currentUser?.email || "המשתמש הנוכחי"}
          </div>
        )}
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>שיוך מתנדבים</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 8 }}>
          <SearchableSelect value={selectedVolunteerId} onChange={(nextVolunteerId) => setSelectedVolunteerId(nextVolunteerId)} options={activeVolunteers} placeholder="בחר מתנדב" searchPlaceholder="חיפוש מתנדב לפי שם / טלפון / עיר" emptyText="לא נמצאו מתנדבים" getOptionValue={(option) => option.id} getOptionLabel={(option) => option.name || "ללא שם"} getOptionKeywords={(option) => [option.firstName, option.lastName, option.phone, option.city].filter(Boolean).join(" ")} />
          <button type="button" style={btnSecondary} onClick={addVolunteerToBeneficiary}><Ic.Plus /> הוסף</button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {f.volunteerIds.map((id) => {
            const v = activeVolunteers.find((x) => x.id === id) || volunteers.find((x) => x.id === id);
            return (
              <button key={id} type="button" onClick={() => removeVolunteerFromBeneficiary(id)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 8, border: `1.5px solid ${ACCENT}`, cursor: "pointer", fontSize: 13, background: ACCENT_LIGHT, color: ACCENT, userSelect: "none" }}
                title="לחץ להסרה"
              >{v?.name || id} ✕</button>
            );
          })}
          {f.volunteerIds.length === 0 && <span style={{ fontSize: 12, color: TEXT_MUTED }}>אין מתנדבים משויכים</span>}
        </div>
      </div>
      <div style={{ marginBottom: 16 }}><label style={labelStyle}>הערות</label><textarea style={{ ...inputStyle, resize: "vertical", minHeight: 70 }} value={f.notes} onChange={(e) => u("notes", e.target.value)} /></div>
      <div style={{ marginBottom: 8 }}><label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14 }}><input type="checkbox" checked={f.active} onChange={(e) => u("active", e.target.checked)} /> פעיל</label></div>
    </Modal>
  );
}
function getContactValue(entry) {
  if (Array.isArray(entry)) return `${entry[0] || ""}`.trim();
  return `${entry || ""}`.trim();
}
